// src/store/workflow/workflowThunks.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
	deleteWorkflowProcess,
	getProcessDocuments,
	getProcessDocumentDownload,
	regenerateProcessDocument,
	getDocumentTemplateVersions,
	getReassignCandidates,
	reassignTask,
	cloneWorkflowProcess,
	startWorkflowProcessInCompany,
	viewWorkflowProcess,
	unclaimPendingTask,
	claimUnassignedTask,
	claimTask,
	startWorkflowProcess,
	getWorkflowsList,
	executePendingTask,
} from "api/workflowsApi";
import { logger } from "helpers/logger";

// Documents generated so far for a process (company from the store; checked by the backend).
export const fetchProcessDocumentsThunk = createAsyncThunk("workflow/fetchProcessDocuments", async ({ processInstanceId }, { getState, rejectWithValue, signal }) => {
	try {
		const companyId = getState().selectedCompany.data?.companyData?.companyId;
		return await getProcessDocuments({ processInstanceId, companyId }, signal);
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to fetch process documents:", error);
		return rejectWithValue(error.message || "Failed to fetch documents");
	}
});

// A short-lived download link for one document (company from the store; checked by the backend).
export const fetchDocumentDownloadThunk = createAsyncThunk("workflow/fetchDocumentDownload", async ({ documentId }, { getState, rejectWithValue, signal }) => {
	try {
		const companyId = getState().selectedCompany.data?.companyData?.companyId;
		return await getProcessDocumentDownload({ documentId, companyId }, signal);
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to get document download link:", error);
		return rejectWithValue(error.message || "Failed to download the document");
	}
});

// The versions of a document's template (company from the store; regenerate_pdf checked by the backend).
export const fetchDocumentTemplateVersionsThunk = createAsyncThunk("workflow/fetchDocumentTemplateVersions", async ({ documentId }, { getState, rejectWithValue, signal }) => {
	try {
		const companyId = getState().selectedCompany.data?.companyData?.companyId;
		return await getDocumentTemplateVersions({ documentId, companyId }, signal);
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to fetch template versions:", error);
		return rejectWithValue(error.message || "Failed to load the template versions");
	}
});

// Regenerates one document as a new version, with the chosen template version (company from the
// store; regenerate_pdf checked by the backend).
export const regenerateProcessDocumentThunk = createAsyncThunk("workflow/regenerateProcessDocument", async ({ documentId, templateVersionId }, { getState, rejectWithValue, signal }) => {
	try {
		const companyId = getState().selectedCompany.data?.companyData?.companyId;
		return await regenerateProcessDocument({ documentId, companyId, templateVersionId }, signal);
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to regenerate document:", error);
		return rejectWithValue(error.message || "Failed to regenerate the document");
	}
});

// The process's open task and who it can be reassigned to (company from the store; checked by the backend).
export const fetchReassignCandidatesThunk = createAsyncThunk("workflow/fetchReassignCandidates", async ({ processInstanceId }, { getState, rejectWithValue, signal }) => {
	try {
		const companyId = getState().selectedCompany.data?.companyData?.companyId;
		return await getReassignCandidates({ processInstanceId, companyId }, signal);
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to fetch reassign candidates:", error);
		return rejectWithValue(error.message || "Failed to load who the task can be reassigned to");
	}
});

// One of the caller's processes, read-only (company from the store; checked by the backend).
export const viewWorkflowProcessThunk = createAsyncThunk("workflow/viewWorkflowProcess", async ({ processInstanceId }, { getState, rejectWithValue, signal }) => {
	try {
		const companyId = getState().selectedCompany.data?.companyData?.companyId;
		return await viewWorkflowProcess({ processInstanceId, companyId }, signal);
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to load process for viewing:", error);
		return rejectWithValue(error.message || "Failed to load workflow history forms");
	}
});

// Starts a workflow in the selected company (company from the store; start_workflow checked by the backend).
export const startWorkflowProcessInCompanyThunk = createAsyncThunk("workflow/startWorkflowProcessInCompany", async ({ processDefinitionKey }, { getState, rejectWithValue, signal }) => {
	try {
		const companyId = getState().selectedCompany.data?.companyData?.companyId;
		return await startWorkflowProcessInCompany({ processDefinitionKey, companyId }, signal);
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to start process:", error);
		return rejectWithValue(error.message || "Failed to start the process");
	}
});

// Clones a process (company from the store; clone_workflow checked by the backend). Pass
// processInstanceId (history row) or processDefinitionKey (the company's latest of that workflow).
export const cloneWorkflowProcessThunk = createAsyncThunk("workflow/cloneWorkflowProcess", async ({ processInstanceId, processDefinitionKey }, { getState, rejectWithValue, signal }) => {
	try {
		const companyId = getState().selectedCompany.data?.companyData?.companyId;
		return await cloneWorkflowProcess({ processInstanceId, processDefinitionKey, companyId }, signal);
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to clone process:", error);
		return rejectWithValue(error.message || "Failed to clone the process");
	}
});

// Gives an open task to another user (company from the store; reassign_task checked by the backend).
export const reassignTaskThunk = createAsyncThunk("workflow/reassignTask", async ({ taskId, newAssignee }, { getState, rejectWithValue, signal }) => {
	try {
		const companyId = getState().selectedCompany.data?.companyData?.companyId;
		return await reassignTask({ taskId, newAssignee, companyId }, signal);
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to reassign task:", error);
		return rejectWithValue(error.message || "Failed to reassign the task");
	}
});

// Pending tasks and workflow history pages: deletes (cancels) a whole process through
// api/WorkflowProcesses/Delete (delete_workflow and the workflow's delete policy checked by the backend).
export const deleteWorkflowProcessThunk = createAsyncThunk("workflow/deleteWorkflowProcess", async ({ processInstanceId }, { getState, rejectWithValue, signal }) => {
	try {
		const companyId = getState().selectedCompany.data?.companyData?.companyId;
		return await deleteWorkflowProcess({ processInstanceId, companyId }, signal);
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to delete process:", error);
		return rejectWithValue(error.message || "Failed to delete process");
	}
});

export const unclaimPendingTaskThunk = createAsyncThunk("workflow/unclaimPendingTaskThunk", async ({ taskId }, { getState, rejectWithValue, signal }) => {
	try {
		const companyId = getState().selectedCompany.data?.companyData?.companyId;
		const response = await unclaimPendingTask({ taskId, companyId }, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to unclaim pending task:", error);
		return rejectWithValue(error.message || "Failed to Unclaim Task");
	}
});

// Unassigned tasks page: claims a task for the logged-in user and returns its Execute steps
// (company from the store; everything checked by the backend).
export const claimUnassignedTaskThunk = createAsyncThunk("workflow/claimUnassignedTask", async ({ taskId }, { getState, rejectWithValue, signal }) => {
	try {
		const companyId = getState().selectedCompany.data?.companyData?.companyId;
		return await claimUnassignedTask({ taskId, companyId }, signal);
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to claim task:", error);
		return rejectWithValue(error.message || "Failed to claim the task");
	}
});

export const claimTaskThunk = createAsyncThunk("workflow/claimTaskThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { taskId, userId } = params;
		const response = await claimTask(taskId, userId, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to claim task:", error);
		return rejectWithValue(error.message || "Failed to Claim Task");
	}
});

export const startWorkflowProcessThunk = createAsyncThunk("workflow/startWorkflowProcessThunk", async (params, { rejectWithValue, signal }) => {
	try {
		const { definitionKey, assignee } = params;
		const response = await startWorkflowProcess(definitionKey, assignee, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to start workflow process:", error);
		return rejectWithValue(error.message || "Failed to Start Workflow Process");
	}
});

export const getWorkflowsListThunk = createAsyncThunk("workflow/getWorkflowsListThunk", async (params, { rejectWithValue }) => {
	try {
		const { userId, companyId } = params;
		const response = await getWorkflowsList(userId, companyId);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to get workflows list:", error);
		return rejectWithValue(error.message || "Failed to Get Workflow List.");
	}
});

export const executePendingTaskThunk = createAsyncThunk("workflow/executePendingTaskThunk", async ({ processInstanceId }, { getState, rejectWithValue, signal }) => {
	try {
		const companyId = getState().selectedCompany.data?.companyData?.companyId;
		const response = await executePendingTask({ processInstanceId, companyId }, signal);
		return response;
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return rejectWithValue("cancelled");
		}
		logger.error("❌ Failed to execute pending task:", error);
		return rejectWithValue(error.message || "Failed to execute pending task");
	}
});
